import { Kunde, SelectOption } from '@gsm/entities';
import { AND_OPERATOR, DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { createOptionsFromEntities } from '../../helpers/options.helper';
import { MATCH_SELECT_VISIBLE } from '../../matchers/select-visible.matcher';
import { TYPE_CHIP_CHOICE_AUTOCOMPLETE } from '../chip-choice-autocomplete/chip-choice-autocomplete.model';
import { TYPE_CHIP_CHOICE_MULTIPLE } from '../chip-choice-multiple/chip-choice-multiple.model';
import { TYPE_CUSTOM_FILE_UPLOAD } from '../custom-file-upload/custom-file-upload.model';
import { TYPE_DATA_ARRAY } from '../data-array/data-array.model';
import { TYPE_DOKUMENTEN_ANGABE } from '../dokumenten-angabe/dokumenten-angabe.model';
import { TYPE_EIGENLEISTUNG_TABLE } from '../eigenleistung-table/eigenleistung-table.model';
import { TYPE_INFO } from '../info/info.model';
import { TYPE_SELECT_FILTER } from '../select-filter/select-filter.model';
import { TYPE_ZAHLUNGSEMPFAENGER } from '../zahlungsempfaenger/zahlungsempfaenger.model';

export function GEBAEUDE_SCHADEN_TEMPLATE_FACTORY(
  gewerke: SelectOption[] = [],
  kunden: Kunde[] = [],
  eigenleistungDokumentTypes: SelectOption[] = [],
  fachfirmaDokumentTypes: SelectOption[] = [],
  schadenHoeheList: SelectOption[] = [],
  updateClaim = false,
  removeRechnungssteller = true
) {
  const gewerkeOptions = createOptionsFromEntities(gewerke, 'label');

  return [
    // wrapper group is unfortunately necessary for MATCH_SELECT_VISIBLE to work
    {
      id:    'gebaeude_schaden_group',
      type:  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      group: [
        {
          id:            'info',
          type:          'INFO',
          content:       'Bitte erfassen Sie hier den/die Gebäudeschäden (zB.: Schäden an Bodenbelag, Malerarbeiten, Trocknung, etc.)',
          isInstruction: true
        },
        {
          id:            'massnahmenBekannt',
          type:          'SELECT',
          label:         'Sind die Maßnahmen zur allfälligen Schadenbehebung bekannt/unbekannt?',
          required:      true,
          disabled:      updateClaim,
          validators:    {
            required: null
          },
          errorMessages: {
            required: 'Auswahl ist ein Pflichtfeld.'
          },
          options:       [
            {
              label: 'Bekannt',
              value: true
            },
            {
              label: 'Unbekannt',
              value: false
            }
          ]
        },
        {
          id:            'schadenbehebung_instruction',
          type:          TYPE_INFO,
          content:       'Wie erfolgt die Schadenbehebung?',
          isInstruction: true,
          relations:     [
            {
              match: MATCH_SELECT_VISIBLE,
              when:  [
                {
                  id:    'massnahmenBekannt',
                  value: true
                }
              ]
            }
          ]
        },
        {
          id:        'schadenbehebung',
          type:      TYPE_CHIP_CHOICE_MULTIPLE,
          multiple:  true,
          label:     'Schadenbehebung erfolgt durch',
          required:  true,
          disabled:  updateClaim,
          relations: [
            {
              match: MATCH_SELECT_VISIBLE,
              when:  [
                {
                  id:    'massnahmenBekannt',
                  value: true
                }
              ]
            }
          ],
          options:   [
            {
              label: 'NV-Partnerfirma',
              value: {
                label: 'NV-Partnerfirma',
                value: 'partnerfirma'
              }
            },
            {
              label: 'Eigenleistung',
              value: {
                label: 'Eigenleistung',
                value: 'eigenleistung'
              }
            },
            {
              label: 'Fachfirma',
              value: {
                label: 'Fachfirma',
                value: 'fachfirma'
              }
            }
          ]
        },
        {
          id:        'partnerfirma_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'NV-Partnerfirma',
          hidden:    true,
          relations: [
            {
              match:    MATCH_SELECT_VISIBLE,
              operator: AND_OPERATOR,
              when:     [
                {
                  id:    'massnahmenBekannt',
                  value: true
                },
                {
                  id:    'schadenbehebung',
                  value: {
                    label: 'NV-Partnerfirma',
                    value: 'partnerfirma'
                  }
                }
              ]
            }
          ],
          layout:    {
            element: {
              host: 'gsm-section-headline-3'
            }
          },
          group:     [
            {
              id:            'partnerfirma_instruction',
              type:          TYPE_INFO,
              content:
                             'Wünscht der Versicherungsnehmer Unterstützung bei der Auswahl der Reparaturfirma seines Schadens?<br><br>Nach Abschluss der Schadenmeldung erhalten Sie per Mail einen Link zugeschickt. Der Link ist 2 Stunden gültig und ermöglicht die direkte Auswahl der gewünschten Reparaturfirma.<br><br>Um eine reibungslose Beauftragung gewährleisten zu können, kontrollieren Sie bitte die Kontaktdaten des Versicherungsnehmers auf Vollständigkeit und Richtigkeit und ändern/ergänzen Sie diese bei Bedarf.',
              isInstruction: true
            },
            {
              id:             'partnerfirmaList',
              type:           TYPE_DATA_ARRAY,
              required:       true,
              editable:       true,
              addButtonLabel: 'Weiteren Eintrag hinzufügen',
              groupPrototype: [
                {
                  id:            'gewerk',
                  type:          TYPE_SELECT_FILTER,
                  label:         'Gewerk',
                  placeholder:   'Bitte wählen Sie ein Gewerk aus.',
                  required:      true,
                  optionMatcher: ['value'],
                  options:       gewerkeOptions,
                  additional:    {
                    immediateValidation: true
                  }
                },
                {
                  id:            'notiz',
                  type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
                  label:         'Notiz',
                  placeholder:   'Notiz hinzufügen',
                  validators:    {
                    maxLength: 300
                  },
                  errorMessages: {
                    maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
                  }
                }
              ],
              layout:         {
                element: {
                  host: 'gsm-compact'
                }
              }
            }
          ]
        },
        {
          id:        'eigenleistung_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Eigenleistung',
          hidden:    true,
          relations: [
            {
              match:    MATCH_SELECT_VISIBLE,
              operator: AND_OPERATOR,
              when:     [
                {
                  id:    'massnahmenBekannt',
                  value: true
                },
                {
                  id:    'schadenbehebung',
                  value: {
                    label: 'Eigenleistung',
                    value: 'eigenleistung'
                  }
                }
              ]
            }
          ],
          layout:    {
            element: {
              host: 'gsm-section-headline-3'
            }
          },
          group:     [
            {
              id:            'vorhanden_instruction',
              type:          TYPE_INFO,
              content:       'Ist eine Eigenleistungsaufstellung vorhanden?',
              isInstruction: true
            },
            {
              id:          'vorhanden',
              type:        DYNAMIC_FORM_CONTROL_TYPE_SELECT,
              placeholder: 'Eigenleistungsaufstellung',
              required:    true,
              options:     [
                {
                  label: 'Ja, es ist eine Eigenleistungsaufstellung vorhanden.',
                  value: 'V'
                },
                {
                  label: 'Ja, eine Eigenleistungsaufstellung wird nachgebracht.',
                  value: 'N'
                },
                {
                  label: 'Eigenleistungsaufstellung erstellen.',
                  value: 'A'
                }
              ]
            },
            {
              id:           'checklistItemFileList',
              type:         TYPE_CUSTOM_FILE_UPLOAD,
              labelTooltip: 'Dokumente hinzufügen',
              required:     true,
              hidden:       true,
              relations:    [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    }
                  ]
                }
              ]
            },
            {
              id:            'vorhanden_gewerke_instruction',
              type:          TYPE_INFO,
              content:       'Bitte geben Sie die Gewerke bekannt, die sich in der Eigenleistungsaufstellung befinden.',
              isInstruction: true,
              relations:     [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    }
                  ]
                }
              ]
            },
            {
              id:            'gewerke',
              type:          TYPE_CHIP_CHOICE_AUTOCOMPLETE,
              label:         'Gewerke',
              placeholder:   'Gewerke auswählen',
              required:      true,
              hidden:        true,
              options:       gewerkeOptions,
              optionMatcher: ['value'],
              relations:     [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    }
                  ]
                }
              ]
            },
            {
              id:         'eigenleistungAufstellung',
              type:       TYPE_EIGENLEISTUNG_TABLE,
              hidden:     true,
              gewerke,
              additional: {
                immediateValidation: true
              },
              relations:  [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'A'
                    }
                  ]
                }
              ]
            },
            {
              id:            'zahlungsempfaenger_instruction',
              type:          TYPE_INFO,
              content:       'Bitte wählen/erfassen Sie den Zahlungsempfänger der Eigenleistungsaufstellung.',
              isInstruction: true,
              hidden:        true,
              relations:     [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    },
                    {
                      id:    'vorhanden',
                      value: 'A'
                    }
                  ]
                }
              ]
            },
            {
              id:                     'zahlungsempfaenger',
              type:                   TYPE_ZAHLUNGSEMPFAENGER,
              kunden,
              removeRechnungssteller: removeRechnungssteller,
              hidden:                 true,
              required:               true,
              additional:             {
                immediateValidation: true
              },
              relations:              [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    },
                    {
                      id:    'vorhanden',
                      value: 'A'
                    }
                  ]
                }
              ]
            },
            {
              id:         'belege',
              type:       TYPE_DOKUMENTEN_ANGABE,
              label:      'Belege zur Eigenleistungsaufstellung.',
              additional: {
                optionalTypes:       eigenleistungDokumentTypes,
                gewerke:             [], // default empty until gewerke selected in Aufstellung
                kunden,
                immediateValidation: true
              },
              hidden:     true,
              relations:  [
                {
                  match: MATCH_VISIBLE,
                  when:  [
                    {
                      id:    'vorhanden',
                      value: 'V'
                    },
                    {
                      id:    'vorhanden',
                      value: 'A'
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          id:        'fachfirma_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Fachfirma',
          hidden:    true,
          relations: [
            {
              match:    MATCH_SELECT_VISIBLE,
              operator: AND_OPERATOR,
              when:     [
                {
                  id:    'massnahmenBekannt',
                  value: true
                },
                {
                  id:    'schadenbehebung',
                  value: {
                    label: 'Fachfirma',
                    value: 'fachfirma'
                  }
                }
              ]
            }
          ],
          layout:    {
            element: {
              host: 'gsm-section-headline-3'
            }
          },
          group:     [
            {
              id:            'fachfirma_list_instruction',
              type:          TYPE_INFO,
              content:
                             'Bei der Auswahl "Fachfirma" ist die Abteilung Service nicht für die Vermittlung einers Unternehmens zuständig, der Versicherungsnehmer ist für die Erbringung der Unterlagen zuständig.',
              isInstruction: true
            },
            {
              id:         'fachfirmaList',
              type:       TYPE_DOKUMENTEN_ANGABE,
              required:   true,
              additional: {
                optionalTypes:       fachfirmaDokumentTypes,
                gewerke,
                kunden,
                immediateValidation: true
              }
            }
          ]
        },
        {
          id:     'uebersicht_zur_ermittlung',
          type:   DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:  'Übersicht zur Ermittlung der Gesamtschadenhöhe',
          hidden: true,
          group:  [
                    [
                      'uebersicht_partnerfirma',
                      'NV-Partnerfirma'
                    ],
                    [
                      'uebersicht_eigenleistung',
                      'Eigenleistung'
                    ],
                    [
                      'uebersicht_fachfirma',
                      'Fachfirma'
                    ]
                  ].map(([id, label]) => ({
            id,
            type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
            label,
            multiple: true,
            disabled: true,
            hidden:   true,
            options:  gewerkeOptions,
            layout:   {
              element: {
                host: 'gsm-flex-fullwidth gsm-big-label'
              }
            }
          }))
        },
        {
          id:        'gesamtschadenhoehe_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Gesamtschadenhöhe ermitteln',
          hidden:    true,
          relations: [
            {
              match: MATCH_SELECT_VISIBLE,
              when:  [
                {
                  id:    'massnahmenBekannt',
                  value: true
                }
              ]
            }
          ],
          group:     [
            {
              id:            'gesamtschadenhoehe_instruction',
              type:          TYPE_INFO,
              isInstruction: true,
              content:
                             'Bitte wählen Sie die Gesamtschadenhöhe des Gebäudeschadens aus. (Ist diese nicht eindeutig ermittelbar, dann wählen Sie einen höheren Betrag aus.)'
            },
            {
              id:            'gesamtschadenhoehe',
              type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
              label:         'Gesamtschadenhöhe',
              required:      true,
              disabled:      updateClaim,
              optionMatcher: ['value'],
              options:       createOptionsFromEntities(schadenHoeheList, 'label')
            }
          ]
        }
      ]
    }
  ];
}
